import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { COMPANY } from "@/legal/company";
import { PAGES, LEGAL_LINKS } from "@/legal/content";

const FIELD_ROWS = [
  ["Company", COMPANY.legalName],
  ["Website", COMPANY.websiteDisplay],
  ["Privacy contact", COMPANY.privacyEmail],
  ["Support contact", COMPANY.supportEmail],
  ["Grievance/Privacy contact person", COMPANY.grievanceOfficer],
  ["Telephone", COMPANY.phone],
  ["Registered office", COMPANY.registeredOffice],
  ["LLPIN", COMPANY.llpin],
  ["GSTIN", COMPANY.gstin],
];

const CompanyBlock = () => {
  // Pending fields keep their centralized placeholder in company.js but stay hidden publicly;
  // they appear automatically once a real value replaces the placeholder.
  const rows = FIELD_ROWS.filter(([, v]) => v && !v.includes("[TO BE UPDATED]"));
  return (
    <section className="mt-10 bg-[#F4F5F7] rounded-2xl p-5" data-testid="legal-company-block">
      <h2 className="font-heading text-lg font-bold text-[#00194E] mb-3">Contact &amp; company information</h2>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-slate-600">
        {rows.map(([label, value]) => (
          <div key={label}><dt className="font-semibold text-slate-700 inline">{label}: </dt><dd className="inline">{value}</dd></div>
        ))}
      </dl>
    </section>
  );
};

export default function LegalPage({ page }) {
  const content = PAGES[page];

  useEffect(() => {
    document.title = `${content.title} — EDUJAC`;
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute("content");
    meta?.setAttribute("content", content.description);
    window.scrollTo(0, 0);
    return () => {
      document.title = "EDUJAC — School ERP & Management Platform";
      if (meta && prev) meta.setAttribute("content", prev);
    };
  }, [page, content]);

  return (
    <div className="min-h-screen bg-[#F4F5F7]" data-testid={`legal-page-${page}`}>
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-white/40">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="legal-brand">
            <img src="/edujac-mark.png" alt="EDUJAC" className="h-9 w-9 object-contain" />
            <span className="font-heading font-extrabold text-xl tracking-wide text-[#00194E]">EDUJAC</span>
          </Link>
          <Link to="/" data-testid="legal-back-home" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#00194E] transition-colors duration-200">
            <ArrowLeft size={15} /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10">
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-[#00194E]" data-testid="legal-title">{content.title}</h1>
          <p className="text-xs font-semibold text-slate-400 mt-2 uppercase tracking-wider" data-testid="legal-last-updated">Last updated: {content.lastUpdated}</p>

          {content.sections.map((s, i) => (
            <section key={i} className="mt-9">
              <h2 className="font-heading text-lg font-bold text-[#00194E] mb-3">{i + 1}. {s.h}</h2>
              {s.body?.map((p, j) => (
                <p key={j} className="text-sm md:text-[15px] text-slate-600 leading-relaxed mb-3">{p}</p>
              ))}
              {s.list && (
                <ul className="list-disc pl-5 space-y-1.5 text-sm md:text-[15px] text-slate-600 leading-relaxed">
                  {s.list.map((li, k) => <li key={k}>{li}</li>)}
                </ul>
              )}
            </section>
          ))}

          <CompanyBlock />
        </div>

        <nav className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2" data-testid="legal-cross-nav">
          {LEGAL_LINKS.filter((l) => l.slug !== page).map((l) => (
            <Link key={l.slug} to={`/${l.slug}`} data-testid={`legal-nav-${l.slug}`} className="text-xs font-semibold text-slate-500 hover:text-[#00194E] transition-colors duration-200">
              {l.label}
            </Link>
          ))}
        </nav>
        <p className="text-center text-xs text-slate-400 mt-6">{COMPANY.legalName}</p>
      </main>
    </div>
  );
}
